import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { matches } from '@/mocks/matches';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="pt-24 px-4 text-center">
        <h2 className="text-xl font-bold text-foreground-900">分类未找到</h2>
        <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const catMatches = matches.filter((m) => m.categoryId === category.id);
  const catArticles = articles.filter((a) => a.categoryId === category.id);
  const liveMatches = catMatches.filter((m) => m.status === 'live');
  const upcomingMatches = catMatches.filter((m) => m.status === 'upcoming');
  const finishedMatches = catMatches.filter((m) => m.status === 'finished');

  return (
    <main className="w-full px-4 md:px-6 pt-24 pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
            {category.name}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
            {category.description}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                cat.slug === slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {liveMatches.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-500 animate-pulse"></span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">正在直播</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveMatches.map((match) => (
                <div key={match.id} className="p-5 rounded-lg bg-background-100 border border-background-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-foreground-500">{match.league}</span>
                    <span className="text-xs font-semibold text-accent-600">{match.time}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex flex-col items-center gap-1.5 flex-1">
                      <img src={match.homeLogo} alt={match.homeTeam} className="w-10 h-10 rounded-full object-cover border-2 border-background-200" loading="lazy" />
                      <span className="text-xs font-medium text-foreground-800 text-center whitespace-nowrap">{match.homeTeam}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xl font-heading font-bold text-foreground-900">{match.homeScore}</span>
                      <span className="text-sm text-foreground-400">:</span>
                      <span className="text-xl font-heading font-bold text-foreground-900">{match.awayScore}</span>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 flex-1">
                      <img src={match.awayLogo} alt={match.awayTeam} className="w-10 h-10 rounded-full object-cover border-2 border-background-200" loading="lazy" />
                      <span className="text-xs font-medium text-foreground-800 text-center whitespace-nowrap">{match.awayTeam}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {upcomingMatches.length > 0 && (
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">即将开始</h2>
            <div className="space-y-3">
              {upcomingMatches.map((match) => (
                <div key={match.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="flex items-center gap-3">
                    <span className="text-sm md:text-base font-semibold text-foreground-900">{match.homeTeam}</span>
                    <span className="text-xs text-foreground-400 font-heading">VS</span>
                    <span className="text-sm md:text-base font-semibold text-foreground-900">{match.awayTeam}</span>
                    <span className="text-xs text-foreground-500">({match.league})</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{match.time}</span>
                    <span className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold whitespace-nowrap cursor-pointer">预约</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {finishedMatches.length > 0 && (
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">已结束</h2>
            <div className="space-y-2">
              {finishedMatches.map((match) => (
                <div key={match.id} className="flex items-center justify-between p-3 rounded-lg bg-background-100 border border-background-200">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground-800">{match.homeTeam}</span>
                    <span className="text-sm font-heading font-bold text-foreground-900">{match.homeScore}-{match.awayScore}</span>
                    <span className="text-sm font-medium text-foreground-800">{match.awayTeam}</span>
                    <span className="text-xs text-foreground-500">({match.league})</span>
                  </div>
                  <span className="text-xs text-foreground-400">{match.time}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {catArticles.length > 0 && (
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关资讯</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {catArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span>{article.publishDate}</span>
                      <span>·</span>
                      <span>{article.views.toLocaleString()} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {catMatches.length === 0 && catArticles.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">暂无相关赛事和资讯</p>
          </div>
        )}
      </div>
    </main>
  );
}